import {createSlice} from "@reduxjs/toolkit";
import type {Dealership, DealerUser} from "@/types";
import {signUpAsync} from "@/store/thunks/authenticationThunks/signUp.ts";
import {loginAsync, restoreSessionAsync} from "@/store/thunks/authenticationThunks/login.ts";
import {createDealer} from "@/store/thunks/authenticationThunks/createDealer.ts";
import {updateDealershipProfile, updateDealerUserProfile} from "@/store/thunks/authenticationThunks/other.ts";

export interface AuthenticationState {
    user?: Dealership;
    dealerUser?: DealerUser;
    loading: boolean;
    error?: string;
    redirectTo?: string;
    onboardingRequired?: boolean;
}

const AUTH_STORAGE_KEY = "wheela:auth";

type PersistedAuthState = Pick<AuthenticationState, "user" | "dealerUser" | "redirectTo" | "onboardingRequired">;

function loadPersistedAuthState(): PersistedAuthState {
    if (typeof window === "undefined") {
        return {};
    }

    try {
        const serialized = window.localStorage.getItem(AUTH_STORAGE_KEY);
        if (!serialized) {
            return {};
        }

        const parsed = JSON.parse(serialized) as PersistedAuthState;
        return {
            user: parsed.user,
            dealerUser: parsed.dealerUser,
            redirectTo: parsed.redirectTo ?? "/",
            onboardingRequired: parsed.onboardingRequired
        };
    } catch {
        return {};
    }
}

function persistAuthState(state: AuthenticationState) {
    if (typeof window === "undefined") {
        return;
    }

    if (!state.user) {
        window.localStorage.removeItem(AUTH_STORAGE_KEY);
        return;
    }

    const toPersist: PersistedAuthState = {
        user: state.user,
        dealerUser: state.dealerUser,
        redirectTo: state.redirectTo ?? "/",
        onboardingRequired: state.onboardingRequired
    };
    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(toPersist));
}

function clearPersistedAuthState() {
    if (typeof window === "undefined") {
        return;
    }
    window.localStorage.removeItem(AUTH_STORAGE_KEY);
}

const persistedAuth = loadPersistedAuthState();

const initialState: AuthenticationState = {
    loading: false,
    redirectTo: persistedAuth.redirectTo ?? "/",
    onboardingRequired: persistedAuth.onboardingRequired,
    user: persistedAuth.user,
    dealerUser: persistedAuth.dealerUser
}

const authenticationSlice = createSlice({
    name: 'authentication',
    initialState,
    reducers: {
        logout(state) {
            state.user = undefined;
            state.dealerUser = undefined;
            state.redirectTo = undefined;
            state.onboardingRequired = undefined;
            state.error = undefined;
            persistAuthState(state);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(signUpAsync.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(signUpAsync.fulfilled, (state, action) => {
                state.user = action.payload.user;
                state.dealerUser = action.payload.dealerUser;
                state.redirectTo = action.payload.redirectTo;
                state.onboardingRequired = action.payload.onboardingRequired;
                state.loading = false;
                state.error = undefined;
                persistAuthState(state);
            })
            .addCase(signUpAsync.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    (action.payload as string) ||
                    action.error.message ||
                    "Signup failed";
            });
        builder
            .addCase(loginAsync.pending,(state) => {
                state.loading = true;
            })
            .addCase(loginAsync.fulfilled,(state, action) => {
                state.user = action.payload.user;
                state.dealerUser = action.payload.dealerUser;
                state.redirectTo = action.payload.redirectTo;
                state.onboardingRequired = action.payload.onboardingRequired;
                state.loading = false;
                state.error = undefined;
                if (action.meta.arg.remember) {
                    persistAuthState(state);
                } else {
                    clearPersistedAuthState();
                }
            })
            .addCase(loginAsync.rejected,(state, action) => {
                state.loading = false;
                state.error =
                    (action.payload as string) ||
                    action.error.message ||
                    "Login failed";
            })
            .addCase(restoreSessionAsync.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(restoreSessionAsync.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload?.user;
                state.dealerUser = action.payload?.dealerUser;
                state.redirectTo = action.payload?.redirectTo;
                state.onboardingRequired = action.payload?.onboardingRequired;
            })
            .addCase(restoreSessionAsync.rejected, (state, action) => {
                state.loading = false;
                state.user = undefined;
                state.dealerUser = undefined;
                state.redirectTo = "/login";
                state.onboardingRequired = undefined;
                state.error =
                    (action.payload as string) ||
                    action.error.message ||
                    "Session restore failed";
            })
            .addCase(createDealer.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(createDealer.fulfilled, (state, action) => {
                state.user = action.payload.dealer;
                state.dealerUser = action.payload.dealerUser;
                state.redirectTo = "/";
                state.onboardingRequired = false;
                state.loading = false;
                state.error = undefined;
                persistAuthState(state);
            })
            .addCase(createDealer.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    (action.payload as string) ||
                    action.error.message ||
                    "Dealer setup failed";
            })
            .addCase(updateDealershipProfile.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(updateDealershipProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.user = action.payload;
                state.error = undefined;
                persistAuthState(state);
            })
            .addCase(updateDealershipProfile.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    (action.payload as string) ||
                    action.error.message ||
                    "Dealership update failed";
            })
            .addCase(updateDealerUserProfile.pending, (state) => {
                state.loading = true;
                state.error = undefined;
            })
            .addCase(updateDealerUserProfile.fulfilled, (state, action) => {
                state.loading = false;
                state.dealerUser = action.payload;
                state.error = undefined;
                persistAuthState(state);
            })
            .addCase(updateDealerUserProfile.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    (action.payload as string) ||
                    action.error.message ||
                    "Dealer user update failed";
            })
    }

});

export const {logout} = authenticationSlice.actions;
export * from '../thunks/authenticationThunks/signUp.ts'
export * from '../thunks/authenticationThunks/login.ts'
export * from '../thunks/authenticationThunks/createDealer.ts'
export * from '../thunks/authenticationThunks/other.ts'
export default authenticationSlice;
